import { AfterViewInit, Directive, EventEmitter, inject, Input, TemplateRef, ViewChild } from '@angular/core';
import { AuthService } from '@klickdata/core/auth';
import { AppScope } from '@klickdata/core/resource';
import { CacheUtils, Utils } from '@klickdata/core/util';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Filter, FilterSpecs } from './filter';
import { FilterCache } from './filter-core';
import { FilterModel } from './filter-model';
@Directive()
export abstract class FilterBase implements FilterCache, AfterViewInit, FilterModel {
    public authService: AuthService = inject(AuthService);
    @ViewChild('activeFilterTemplate') activeTemplate: TemplateRef<any>;
    @ViewChild('activeFilterTemplateMultiple') activeTemplateMultiple: TemplateRef<any>;
    /**
     * When you use this input, don't forget
     * to put the local variable selector
     * check any implementation for checking.
     */
    @Input() set filterSpecs(filterSpecs: FilterSpecs) {
        this._filter = { ...this._filter, ...filterSpecs };
    }
    abstract filterChange: EventEmitter<Filter<string | number>>;
    filterRemoval: EventEmitter<Filter<string | number>>;
    protected abstract _filter: Filter<string | number>;
    private cached: any;
    private ـcacheScope: AppScope;
    public get cacheScope(): AppScope {
        return this.ـcacheScope;
    }
    public set cacheScope(value: AppScope) {
        this.ـcacheScope ??= value;
    }
    get filter(): Filter<string | number> {
        return this._filter;
    }
    protected setFilterItems(items: any) {
        this.filter.items = items;
    }

    ngAfterViewInit(): void {
        this.filter.template = this.activeTemplate;
        this.filter.templateMultiple = this.activeTemplateMultiple;
    }

    public mapFilterChange(
        predicate: (value: Filter<string | number>) => boolean,
        apply: (fltr: Filter<string | number>) => void
    ): Observable<Filter<string | number> | Filter<string>> {
        return this.filterChange.pipe(
            map((value) => {
                if (predicate(value)) {
                    this.handleComposite(apply);
                    this.addToCache();
                } else {
                    this.setFilterItems(this.cached);
                }
                return this.filter;
            })
        );
    }

    private handleComposite(apply: (fltr: Filter<string | number>) => void) {
        if (this.filter.isMultiple) {
            const items = this.cached ? this.handleMultipleItems() : this.filter.items;
            this.cached = items;
            this.filter.items = items;
            apply(this.filter);
        } else {
            if (this.filter.composite) {
                this.filter.composite.forEach((fltr) => {
                    apply(fltr); // pass param to closure fn.
                });
            } else {
                apply(this.filter);
            }
        }
    }
    private handleMultipleItems(): any[] {
        // When remove some queries
        if (this.filter.isPartialRemove) {
            return this.filter.items;
        } else {
            return [...new Set([...this.filter.items, ...this.cached])];
        }
    }

    public mapCache(apply: (fltr: Filter<string | number>) => void): Filter<string | number> {
        const cached = this.fromCache(this.getFilterProperty());
        if (!Utils.isEmpty(cached) && !this.isUserPlatform()) {
            this.cached = cached;
            this.setFilterItems(cached);
            this.handleComposite(apply);
            this.onCacheLoaded(this.cached);
            return this.filter;
        }
    }

    protected setFilter(fltr: Filter<string | number>) {
        this.setFilterItems(fltr.items);
    }

    public mapByFilter(
        filter: Filter<string | number>,
        apply: (fltr: Filter<string | number>) => void,
        addToCache?: boolean
    ): Filter<string | number> {
        this.setFilter(filter);
        this.handleComposite(apply);
        if (addToCache) {
            this.addToCache();
        }
        return this.filter;
    }

    protected onCacheLoaded(items: any) {}

    protected getFilterProperty(): string {
        return this.filter.property;
    }

    /**
     * update user local cache with latest filter values.
     * @param property filter property name.
     * @param value filter items values
     */
    private addToCache() {
        if (!this.isIgnoreCache() && !this.isUserPlatform()) {
            CacheUtils.setCache(`table-filter-${this.cacheScope}-${this.filter.property}`, this.getItemsForCache());
        } else {
            this.removeFromCache(this.filter);
        }
    }

    protected ignoreCache(): boolean {
        return false;
    }
    private isIgnoreCache(): boolean {
        return this.ignoreCache() || this.cacheScope === null;
    }
    private isUserPlatform(): boolean {
        return this.authService.checkPlatform('user');
    }
    protected getItemsForCache(): any {
        return this.filter.items;
    }

    protected fromCache(property: string) {
        return CacheUtils.getFromCache(`table-filter-${this.cacheScope}-${property}`);
    }

    public removeFromCache(fltr: Filter<string | number>) {
        CacheUtils.removeFromCache(`table-filter-${this.cacheScope}-${fltr.property}`);
    }

    public remove() {
        this.removeFromCache(this.filter);
        this.clear();
    }
    public getFilter(): Filter<string | number> {
        return this._filter;
    }
    public abstract clear(): void;
    public selectAll(): void {}
    public setQuery(query: string): void {}
    public setChipId(id: number): void {}
    public setSelectedOption(value: string | number): void {}
    public onToggleAdvancedFiltering(isAdvancedFiltering: boolean, isFromCache = false): void {}

    public equals(filterComp: FilterBase | Filter<string | number>): boolean {
        if (filterComp instanceof FilterBase) {
            return this.filter.property === filterComp.filter.property;
        } else {
            return this.filter.property === filterComp.property;
        }
    }
}
